import { IAssetBase } from "../core/loader/loader";
import { AssetBatch } from "../core/loader/loader.batch";
import ContentBase from "../content/core/content.base";
import AppBabylon from "./app.babylon";

//----------------------------------------------------------------
export class AppContentConfig {
  public loadBatchSize?: number;
}

//----------------------------------------------------------------
// Babylon app with content/asset management. The asset-loading logic lives in
// AssetBatch (core/loader/loader.batch.ts); this class is a thin, backwards-
// compatible facade that delegates to it while preserving the public/protected
// API (loadContents/unloadContents/loadAsset/… + the overridable
// loadAssetComplete hook) that subclasses depend on.
export default class AppContent extends AppBabylon {

  protected contentConfig: AppContentConfig = new AppContentConfig();

  protected batch: AssetBatch = new AssetBatch({
    getScene: () => this.scene,
    getRootNode: () => this.rootNode,
    getBatchSize: () => this.contentConfig.loadBatchSize,
    onAssetLoadComplete: (asset: IAssetBase) => this.loadAssetComplete(asset),
  });

  // active runnable content (replaced/torn down via setContent/clearContent).
  protected content?: ContentBase;

  //----------------------------------------------------------------
  constructor(renderCanvas: HTMLCanvasElement) {
    super(renderCanvas);
  }

  public setConfigContent(contentConfig: AppContentConfig) {
    this.contentConfig = contentConfig;
  }

  //----------------------------------------------------------------
  // content runner. setContent swaps in a ContentBase instance (init receives
  // the app's scene/camera/rootNode); the app's render/resize/dispose hooks
  // drive its lifecycle. content owns its own teardown via dispose(). the app
  // owns scene.render(), so content.render() is a per-frame update (not a draw).
  public async setContent(content: ContentBase) {
    this.clearContent();
    this.content = content;
    await content.init(this.engine!, this.scene!, this.camera!, this.rootNode);
  }

  public clearContent() {
    this.content?.dispose();
    this.content = undefined;
  }

  //----------------------------------------------------------------
  protected override render() {
    super.render();
    this.content?.render(this.timeDelta);
  }

  protected override resize() {
    super.resize();
    this.content?.resize();
  }

  public override dispose() {
    this.clearContent();
    super.dispose();
  }

  //----------------------------------------------------------------
  // asset batching.
  // this is legacy code that will be removed in the future.
  // loading can now be done by individual loader functions.
  protected get assets(): IAssetBase[] {
    return this.batch.assets; // loaded asset registry, exposed for subclasses that read it.
  }

  protected get assetsLoading(): IAssetBase[] {
    return this.batch.assetsLoading; // assets queued/in-flight for loading.
  }

  //----------------------------------------------------------------
  public async loadContents(assetsToLoad: IAssetBase[]) {
    await this.batch.loadContents(assetsToLoad);
  }

  public unloadContents(assetsToUnload: IAssetBase[]) {
    this.batch.unloadContents(assetsToUnload);
  }

  //----------------------------------------------------------------
  protected async loadAssets(assetsToLoad: IAssetBase[]) {
    await this.batch.loadAssets(assetsToLoad);
  }

  protected async loadAsset(assetToLoad: IAssetBase) {
    await this.batch.loadAsset(assetToLoad);
  }

  protected loadAssetComplete(_asset: IAssetBase) {
    // override in subclass.
  }

  //----------------------------------------------------------------
  protected unloadAssets(assetsToUnload: IAssetBase[]) {
    this.batch.unloadAssets(assetsToUnload);
  }

  protected unloadAsset(assetToUnload: IAssetBase) {
    this.batch.unloadAsset(assetToUnload);
  }
}
